import {useCallback, useEffect, useState} from 'react';
import {Edge, Node, useReactFlow} from '@xyflow/react';

const defaultOptions = {
	maxHistorySize: 2,
	enableShortcuts: false
};

// https://redux.js.org/usage/implementing-undo-history
export const useUndoRedo = ({
	maxHistorySize = defaultOptions.maxHistorySize,
	enableShortcuts = defaultOptions.enableShortcuts
} = defaultOptions) => {
	// the past and future arrays store the states that we can jump to
	const [past, setPast] = useState([]);

	const {setNodes, setEdges, getNodes, getEdges} = useReactFlow();

	const takeSnapshot = useCallback(() => {
		// push the current graph to the past state
		setPast((past) => [
			...past.slice(past.length - maxHistorySize + 1, past.length),
			{nodes: getNodes(), edges: getEdges()}
		]);
	}, [getNodes, getEdges, maxHistorySize]);

	const undo = useCallback(() => {
		// get the last state that we want to go back to
		const pastState = past[past.length - 1];

		if (pastState) {
			// first we remove the state from the history
			setPast([]);
			const sortParents = () => {
				const parents = pastState.nodes.filter((node) => node.type === 'CustomParentNode');
				const children = pastState.nodes.filter((node) => node.type !== 'CustomParentNode');
				return [...parents, ...children];
			};
			setNodes(sortParents());
			setEdges(pastState.edges);
		}
	}, [setNodes, setEdges, getNodes, getEdges, past]);

	useEffect(() => {
		// this effect is used to attach the global event handlers
		if (!enableShortcuts) {
			return;
		}

		const keyDownHandler = (event) => {
			if (event.key === 'z' && (event.ctrlKey || event.metaKey) && event.shiftKey) {
				return;
			} else if (event.key === 'z' && (event.ctrlKey || event.metaKey)) {
				undo();
			}
		};

		document.addEventListener('keydown', keyDownHandler);

		return () => {
			document.removeEventListener('keydown', keyDownHandler);
		};
	}, [undo, enableShortcuts]);

	const resetRevert = () => {
		setPast([]);
	};

	return {
		undo,
		takeSnapshot,
		canUndo: !past.length,
		resetRevert
	};
};

export default useUndoRedo;
